// Which price-list templates can be applied from a Gestión account, and how.
// Policy decision DG-1 = A (2026-10-14): see plans/fansbury-tasks/F1-0-debt-trigger-matrix.md,
// section "What is automatic vs manual after F1". A future policy change only edits APPLY_POLICY.
// Card fees, social-club-fee and unknown types are deliberately absent: they are never offered.
export const APPLY_POLICY = Object.freeze({
  "custom-fee": { mode: "free", accounts: ["team", "player", "generic"] },
  "protest-fee": { mode: "free", accounts: ["team", "player", "generic"] },
  "tournament-entry-fee": { mode: "event", eventType: "lineup_register", accounts: ["team"] },
  "match-fee": { mode: "event", eventType: "match", accounts: ["team"] },
  "sanction-fee": { mode: "event", eventType: "sanction", accounts: ["player"] },
});

// Types the manual-charge wizard must not offer: entry/match fees need an event (use Apply price),
// card fees are generated when the match is finished, membership fees (social-club-fee) are
// managed only in Socios.
export const MANUAL_DEBT_EXCLUDED_TYPES = [
  "tournament-entry-fee",
  "match-fee",
  "yellow-card-fee",
  "double-yellow-card-fee",
  "red-card-fee",
  "blue-card-fee",
  "social-club-fee",
];

export const manualDebtExcludedTypes = () => [...MANUAL_DEBT_EXCLUDED_TYPES];

const TYPE_ORDER = Object.keys(APPLY_POLICY);
const TEAM_ENTITY_ID = /^team:(\d+)$/;
const ROSTER_MEMBER_ID = /^roster-member:(\d+)$/;

const policyFor = (type) =>
  Object.prototype.hasOwnProperty.call(APPLY_POLICY, type) ? APPLY_POLICY[type] : null;

export const accountKind = (account) => {
  if (account?.type === "individual" && account.entityId === "roster-members") return "player";
  if (account?.type !== "individual" && String(account?.entityId || "").startsWith("team:")) {
    return "team";
  }
  return "generic";
};

export const accountTeamId = (account) =>
  TEAM_ENTITY_ID.exec(String(account?.entityId ?? ""))?.[1] ?? null;

export const accountRosterMemberId = (account) =>
  ROSTER_MEMBER_ID.exec(String(account?.individualId ?? ""))?.[1] ?? null;

export const applyModeFor = (template, account) => {
  const policy = policyFor(template?.type);
  if (!policy || !policy.accounts.includes(accountKind(account))) return null;
  return policy.mode;
};

export const applicableTemplates = (templates, account) =>
  (templates || [])
    .filter((template) => applyModeFor(template, account) !== null)
    .sort(
      (a, b) =>
        TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type) ||
        String(a.name ?? "").localeCompare(String(b.name ?? "")),
    );

export const templateTournamentId = (template) =>
  template.external_relationship_id == null ? null : String(template.external_relationship_id);

export const buildApplyPayload = (template, event) => {
  const policy = policyFor(template?.type);
  if (!policy) throw new Error("template not applicable");
  if (policy.mode === "free") return {};
  if (!event) throw new Error("event required");
  return { event_type: policy.eventType, event_id: Number(event.id) };
};

export const applyChargeErrorMessageId = (error) => {
  const status = error?.response?.status;
  const errors = error?.response?.data?.errors;
  if (status === 422 && errors) {
    if (errors.template) return "gestion.applyPrice.error.template";
    if (errors.event_id) return "gestion.applyPrice.error.eventNotFound";
    if (errors.event_type) return "gestion.applyPrice.error.event";
  }
  return "gestion.error.generic";
};
